import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { differenceInDays } from 'date-fns/differenceInDays';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';

import { AlertContext } from '../contexts/alert-context';
import { type Product, type SaleStatDays, saleStat } from '../data/product';
import { productsQueryOptions } from '../query/query-options';
import { storeLayout } from '../router';
import { useFuseSearch } from './use-fuse-search';

export function useProducts() {
  const { addErrorAlert } = useContext(AlertContext);
  const { store, storeService } = storeLayout.useRouteContext();
  const queryClient = useQueryClient();

  const { data: products = [], isLoading } = useQuery({
    ...productsQueryOptions(store.id),
    queryFn: () => {
      return storeService.getProductsByStore(store.id).catch((error: Error) => {
        addErrorAlert(error.message);

        throw error;
      });
    },
  });

  const { mutateAsync: updateProduct, isPending: saving } = useMutation({
    mutationFn: (product: Product) => {
      return storeService.updateProduct(product.store_id, product).catch((error: Error) => {
        addErrorAlert(error.message);
        throw error;
      });
    },

    onSuccess: (updatedProduct: Product) => {
      // Update the query cache with the updated product
      queryClient.setQueryData(
        ['stores', updatedProduct.store_id, 'products'],
        (oldProducts: Product[] = []) => {
          // The product update endpoint does not fetch the vendor name / pricing information,
          // unlike the list endpoint, so we need to update only select fields
          return oldProducts.map((v) =>
            v.id === updatedProduct.id
              ? {
                  ...v,
                  min_qoh: updatedProduct.min_qoh,
                  disabled: updatedProduct.disabled,
                  disabled_until: updatedProduct.disabled_until,
                  disabled_toggled_at: updatedProduct.disabled_toggled_at,
                  notes: updatedProduct.notes,
                  label_ids: updatedProduct.label_ids,
                  family_id: updatedProduct.family_id,
                }
              : v
          );
        }
      );

      return updatedProduct;
    },
  });

  return {
    products,
    // Only show loading if we don't have data AND we're actually loading (not just fetching)
    loading: products.length === 0 && isLoading,
    saving,
    updateProduct,
  };
}

export type Filters = {
  departments?: string[];
  hideDisabled?: boolean;
  maximumInventory?: number;
  minimumInventory?: number;
  maximumDOH?: number;
  notSoldIn?: SaleStatDays;
  query?: string;
  soldIn?: SaleStatDays;
  stockingIssue?: boolean;
  types?: string[];
  vendorNames?: string[];
  sizes?: string[];
  displayInCases?: boolean;
  labelIds?: number[];
};

const productSearchFields = [
  { name: 'item_code' as const, exact: true },
  { name: 'suggested_item_code' as const, exact: true },
  { name: 'barcode' as const, exact: true },
  { name: 'vendor_name' as const },
  { name: 'brand' as const },
  { name: 'description' as const },
  { name: 'department' as const },
  { name: 'type' as const },
  { name: 'size' as const },
];

const getProductId = (product: Product) => product.id;

export function useQueryFilterFunction(query: string | undefined, products: Product[]) {
  return useFuseSearch(query, products, productSearchFields, getProductId);
}

const today = new Date();

function isPhantom(product: Product, notSoldIn: SaleStatDays) {
  return (
    saleStat(product, notSoldIn) === 0 &&
    (!product.last_received || differenceInDays(today, product.last_received) >= notSoldIn)
  );
}

const MIN_SALE_DAYS = 7;
const STDDEV_MULTIPLIER = 2;

export function stockingIssueThreshold(product: Product): number {
  return product.stats.avg_sales_interval + STDDEV_MULTIPLIER * product.stats.sales_interval_stddev;
}

export function daysSinceLastSale(product: Product): number {
  if (!product.last_sold) {
    return Number.POSITIVE_INFINITY;
  }

  return differenceInDays(today, product.last_sold);
}

const MIN_DAYS_OVERDUE = 2;

export function hasStockingIssue(product: Product): boolean {
  if (product.stats.sale_day_count < MIN_SALE_DAYS) {
    return false;
  }

  if (product.stats.avg_sales_interval <= 0) {
    return false;
  }

  if (product.inventory_qty <= 0) {
    return false;
  }

  if (daysSinceLastSale(product) <= stockingIssueThreshold(product)) {
    return false;
  }

  return daysOverdue(product) >= MIN_DAYS_OVERDUE;
}

export function daysOverdue(product: Product): number {
  return daysSinceLastSale(product) - product.stats.avg_sales_interval;
}

/**
 * Scope filters define the *universe* a page shows: report scoping
 * (sold/notSold/inventory/DOH/stocking-issue) and the hide-disabled setting.
 * They are not "the user narrowing a list" — clearing the search box and the
 * Filters popover should NOT change these — so the result count treats this as
 * the baseline ("X of <baseline>").
 */
function passesScopeFilters(product: Product, filters: Filters): boolean {
  const {
    hideDisabled,
    maximumInventory,
    minimumInventory,
    maximumDOH,
    notSoldIn,
    soldIn,
    stockingIssue,
  } = filters;

  if (soldIn && saleStat(product, soldIn) <= 0) {
    return false;
  }

  // negative inventory report
  if (maximumInventory !== undefined && product.inventory_qty > maximumInventory) {
    return false;
  }

  if (minimumInventory !== undefined && product.inventory_qty < minimumInventory) {
    return false;
  }

  // low stock report
  if (
    maximumDOH !== undefined &&
    (product.stats.days_on_hand < 0 || product.stats.days_on_hand > maximumDOH)
  ) {
    return false;
  }

  // phantom inventory report
  if (notSoldIn && !isPhantom(product, notSoldIn)) {
    return false;
  }

  // stocking issues report
  if (stockingIssue && !hasStockingIssue(product)) {
    return false;
  }

  if (hideDisabled === true && (product.disabled || product.auto_disabled)) {
    return false;
  }

  return true;
}

/**
 * Narrowing filters are the user actively reducing the list — the search box
 * (handled separately) and the Filters popover selections. When any of these
 * is active the count switches to "X of <baseline>".
 */
function passesNarrowingFilters(product: Product, filters: Filters): boolean {
  const { departments, vendorNames, types, sizes, labelIds } = filters;

  if (departments && departments.length > 0 && !departments.includes(product.department)) {
    return false;
  }

  if (vendorNames && vendorNames.length > 0 && !vendorNames.includes(product.vendor_name)) {
    return false;
  }

  if (types && types.length > 0 && !types.includes(product.type)) {
    return false;
  }

  if (sizes && sizes.length > 0 && !sizes.includes(product.size)) {
    return false;
  }

  if (
    labelIds &&
    labelIds.length > 0 &&
    !product.label_ids.some((labelId) => labelIds.includes(labelId))
  ) {
    return false;
  }

  return true;
}

export function useFilteredProducts(products: Product[], initialFilters: Filters) {
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [debouncedQuery] = useDebounce(filters.query, 250);
  const queryFilterFunction = useQueryFilterFunction(debouncedQuery, products);

  // Derive a snapshot of filters that uses the debounced query in place of the
  // live one. Driving the heavy memo off this snapshot — instead of `filters`,
  // which changes on every keystroke — keeps `filteredProducts` referentially
  // stable between debounce fires, so TanStack doesn't rebuild the row model
  // (and the table doesn't re-render) on every keystroke. Popover/scope
  // changes are *not* debounced: any non-query field change invalidates the
  // memo immediately. Keep the deps list aligned with Filters.
  const debouncedFilters = useMemo<Filters>(
    () => ({
      departments: filters.departments,
      hideDisabled: filters.hideDisabled,
      maximumInventory: filters.maximumInventory,
      minimumInventory: filters.minimumInventory,
      maximumDOH: filters.maximumDOH,
      notSoldIn: filters.notSoldIn,
      query: debouncedQuery,
      soldIn: filters.soldIn,
      stockingIssue: filters.stockingIssue,
      types: filters.types,
      vendorNames: filters.vendorNames,
      sizes: filters.sizes,
      displayInCases: filters.displayInCases,
      labelIds: filters.labelIds,
    }),
    [
      filters.departments,
      filters.hideDisabled,
      filters.maximumInventory,
      filters.minimumInventory,
      filters.maximumDOH,
      filters.notSoldIn,
      debouncedQuery,
      filters.soldIn,
      filters.stockingIssue,
      filters.types,
      filters.vendorNames,
      filters.sizes,
      filters.displayInCases,
      filters.labelIds,
    ]
  );

  const { filteredProducts, baselineCount } = useMemo(() => {
    const inScope = products.filter((product) => passesScopeFilters(product, debouncedFilters));
    const filtered = inScope.filter(
      (product) => passesNarrowingFilters(product, debouncedFilters) && queryFilterFunction(product)
    );

    return { filteredProducts: filtered, baselineCount: inScope.length };
  }, [products, debouncedFilters, queryFilterFunction]);

  const clearAll = useCallback(() => {
    setFilters((current) => ({ ...initialFilters, displayInCases: current.displayInCases }));
  }, [initialFilters]);

  return {
    filteredProducts,
    baselineCount,
    filters,
    setFilters,
    clearAll,
  };
}
